import { useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Collapse,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

const customStyles: Record<string, SxProps<Theme>> = {
  expandIcon: {
    marginRight: '10.5px',
  },
  folderHeader: {
    height: '58px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0px 20.5px 0px 18.5px',
    borderRadius: '8px',
    borderColor: '#DDE0E5',
    borderWidth: '5px',
    cursor: 'pointer',
    transitionProperty: 'box-shadow',
    borderStyle: 'none',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'inherit',
  },
  paper: {
    '& .MuiList-root': {
      padding: '0px 8px',
    },
    '& .MuiMenu-paper': {
      borderRadius: '8px',
      padding: '8px 0px',
    },
  },
  folderPaper: {
    borderRadius: '8px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '8px',
  },
  iconAndName: {
    display: 'flex',
  },
  folderText: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '10.5px',
    rowGap: '2px',
    '& h3': {
      fontWeight: 500,
    },
  },
  menuItem: {
    height: '22px',
    borderRadius: '4px',
    padding: '0px 4px 0px 4px',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: '0px',
    },
  },
  users: {
    textTransform: 'lowercase',
  },
  menuItemText: {
    lineHeight: '22px',
  },
  menuIcon: {
    minWidth: '0px',
    marginRight: '9.5px',
  },
};

interface FolderProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  loading: boolean;
  icon: React.ReactNode;
  optionsMenu: Array<{ name: string; action: () => void; icon: JSX.Element }>;
  onOpen?: () => void;
  onClose?: () => void;
}

export function DropdownFolder({
  title,
  subtitle,
  children,
  icon,
  headerContent,
  optionsMenu,
  onClose: handleOnClose,
  onOpen: handleOnOpen,
}: FolderProps) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleClickFolder = () => {
    if (open) {
      setOpen(false);
      if (handleOnClose) handleOnClose();
    } else {
      setOpen(true);
      if (handleOnOpen) handleOnOpen();
    }
  };

  return (
    <>
      <Paper
        sx={customStyles.folderPaper}
        elevation={1}
      >
        <Box
          sx={customStyles.folderHeader}
          onClick={handleClickFolder}
        >
          <Box sx={customStyles.iconAndName}>
            <Box sx={customStyles.expandIcon}>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Box>
            {icon}
            <Box sx={customStyles.folderText}>
              <Typography
                variant='h3'
                color='textSecondary'
              >
                {title}
              </Typography>
              <Typography
                variant='h4'
                color='textSecondary'
                sx={customStyles.users}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <Box sx={customStyles.headerContent}>
            {headerContent ?? <></>}
            <IconButton
              onClick={handleOpenMenu}
              size='large'
            >
              <MoreHorizIcon />
            </IconButton>
          </Box>
        </Box>
        <Collapse
          in={open}
          unmountOnExit
        >
          {children}
        </Collapse>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        sx={customStyles.paper}
      >
        {optionsMenu.map(({ name, action, icon }, index) => (
          <MenuItem
            key={`dropdown-${index}`}
            sx={customStyles.menuItem}
            onClick={(event) => {
              action();
              setAnchorEl(null);
              event.stopPropagation();
            }}
            disableGutters
          >
            <ListItemIcon sx={customStyles.menuIcon}>{icon}</ListItemIcon>
            <Typography sx={customStyles.menuItemText}>{name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
